import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { Card, SectionTitle } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Upload, Plus, FileSpreadsheet, AlertTriangle, Info, ShieldAlert, Download, Eye, Trash2, Loader2, Banknote, ChevronRight } from "lucide-react";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(n || 0);
const pctFmt = (p) => `${((p || 0) * 100).toFixed(2)}%`;

const SEV = {
  critica: { icon: ShieldAlert, cls: "bg-red-50 border-red-200 text-red-800", chip: "bg-red-600" },
  atencion: { icon: AlertTriangle, cls: "bg-amber-50 border-amber-200 text-amber-800", chip: "bg-amber-500" },
  info: { icon: Info, cls: "bg-sky-50 border-sky-200 text-sky-800", chip: "bg-sky-500" },
};

const EST_STYLE = {
  confirmada: "bg-cyan-50 text-cyan-700",
  aprobada: "bg-emerald-50 text-emerald-700",
  pagada: "bg-slate-100 text-slate-600",
};

function AlertasPanel({ alertas }) {
  if (!alertas?.length) return <p className="text-xs text-emerald-600 font-semibold" data-testid="sin-alertas">Sin alertas: el archivo cuadra con las validaciones.</p>;
  return (
    <div className="space-y-2" data-testid="est-alertas-panel">
      {alertas.map((a, i) => {
        const s = SEV[a.severidad] || SEV.info;
        const Icon = s.icon;
        return (
          <div key={i} className={`flex gap-2 items-start border rounded-lg px-3 py-2 text-xs ${s.cls}`} data-testid={`alerta-${a.codigo}`}>
            <Icon size={14} className="mt-0.5 shrink-0" />
            <div>
              <span className={`inline-block text-[9px] font-bold text-white px-1.5 py-0.5 rounded mr-1.5 uppercase ${s.chip}`}>{a.severidad}</span>
              {a.bloqueante && <span className="inline-block text-[9px] font-bold text-white bg-black px-1.5 py-0.5 rounded mr-1.5 uppercase">Bloqueante</span>}
              {a.mensaje}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ContratoModal({ pid, contrato, open, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(contrato || {}); }, [contrato, open]);
  const guardar = async (e) => {
    e.preventDefault();
    try {
      const body = { contratista: f.contratista, nombre_proyecto: f.nombre_proyecto || null, niveles: f.niveles || null,
        fecha_inicio: f.fecha_inicio || null, fecha_fin_programada: f.fecha_fin_programada || null,
        retenciones_pct: f.retenciones_pct ? Number(f.retenciones_pct) : null, notas: f.notas || null };
      if (contrato?.id) await api.patch(`/contratos-obra/${contrato.id}`, body);
      else await api.post(`/properties/${pid}/contratos-obra`, body);
      toast.success(contrato?.id ? "Contrato actualizado" : "Contrato de obra creado");
      onSaved(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="contrato-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">{contrato?.id ? "Editar contrato de obra" : "Nuevo contrato de obra"}</DialogTitle></DialogHeader>
        <form onSubmit={guardar} className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label>Contratista *</Label>
            <Input data-testid="contrato-contratista-input" required value={f.contratista || ""} onChange={(e) => setF({ ...f, contratista: e.target.value })} placeholder="Ej. MENESSE CONSTRUCCIÓN" /></div>
          <div className="space-y-1.5"><Label>Nombre del proyecto</Label>
            <Input value={f.nombre_proyecto || ""} onChange={(e) => setF({ ...f, nombre_proyecto: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Niveles</Label>
            <Input value={f.niveles || ""} onChange={(e) => setF({ ...f, niveles: e.target.value })} placeholder="Ej. 9 NIVELES" /></div>
          <div className="space-y-1.5"><Label>Fecha inicio</Label>
            <Input type="date" value={f.fecha_inicio || ""} onChange={(e) => setF({ ...f, fecha_inicio: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Fin programado</Label>
            <Input type="date" value={f.fecha_fin_programada || ""} onChange={(e) => setF({ ...f, fecha_fin_programada: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>% retenciones</Label>
            <Input type="number" step="0.01" min="0" value={f.retenciones_pct ?? ""} onChange={(e) => setF({ ...f, retenciones_pct: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Notas</Label>
            <Input value={f.notas || ""} onChange={(e) => setF({ ...f, notas: e.target.value })} /></div>
          <div className="col-span-2 flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button data-testid="contrato-guardar-btn" type="submit" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Guardar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmacionModal({ analisis, contrato, onClose, onConfirmado }) {
  const prop = analisis?.propuesta;
  const [car, setCar] = useState(null);
  const [partidas, setPartidas] = useState([]);
  const [baseOk, setBaseOk] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!prop) return;
    setCar(JSON.parse(JSON.stringify(prop.caratula || {})));
    setPartidas(JSON.parse(JSON.stringify(prop.partidas || [])));
    setBaseOk(false);
  }, [analisis]); // eslint-disable-line
  if (!analisis || !prop || !car) return null;

  const bloqueantes = (analisis.alertas || []).filter((a) => a.bloqueante);
  const confBaja = (analisis.confianza?.cuerpo ?? 1) < 0.8;
  const totalPresupuesto = partidas.reduce((s, p) => s + p.subpartidas.filter((x) => x.decision !== "descartar").reduce((s2, x) => s2 + (Number(x.importe_presupuesto) || 0), 0), 0);
  const totalEjecutado = partidas.reduce((s, p) => s + p.subpartidas.filter((x) => x.decision !== "descartar").reduce((s2, x) => s2 + (Number(x.importe_presupuesto) || 0) * (Number(x.pct_periodo) || 0), 0), 0);

  const setSub = (pi, si, patch) => setPartidas((ps) => ps.map((p, i) => i !== pi ? p : { ...p, subpartidas: p.subpartidas.map((x, j) => j !== si ? x : { ...x, ...patch }) }));
  const setCarField = (k, v) => setCar((c) => ({ ...c, [k]: v }));
  const setMin = (k, v) => setCar((c) => ({ ...c, ministracion: { ...(c.ministracion || {}), [k]: v } }));

  const confirmar = async () => {
    setSaving(true);
    try {
      await api.post(`/est-analisis/${analisis.id}/confirmar`, {
        caratula: { ...car, numero_estimacion: Number(car.numero_estimacion) },
        partidas, confirmar_linea_base: baseOk,
      });
      toast.success(`Estimación No. ${car.numero_estimacion} confirmada y guardada`);
      onConfirmado(); onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const descartar = async () => {
    if (!window.confirm("¿Descartar este análisis? El archivo subido se eliminará.")) return;
    try { await api.delete(`/est-analisis/${analisis.id}`); onClose(); } catch (err) { toast.error(apiError(err)); }
  };

  const CarInput = ({ label, k, type = "text", money }) => (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wide text-slate-400">{label}</Label>
      <Input data-testid={`car-${k}`} type={type} className="h-8 text-xs" value={car[k] ?? ""}
        onChange={(e) => setCarField(k, type === "number" ? e.target.value : e.target.value)} />
      {money && car[k] != null && <p className="text-[10px] text-slate-400">{mxn(Number(car[k]))}</p>}
    </div>
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="est-confirm-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-[#002B49] flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-[#00A3B5]" /> Revisión de estimación — confirmación obligatoria
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">{analisis.nombre_original} · leído por {analisis.metodo === "ia" ? "IA (Gemini)" : "código (formato reconocido)"} · la IA nunca guarda: tú confirmas.</p>

        <div className="border rounded-xl p-3 bg-slate-50/60">
          <SectionTitle>Alertas detectadas ({(analisis.alertas || []).length})</SectionTitle>
          <AlertasPanel alertas={analisis.alertas} />
        </div>

        {prop.es_primera && (
          <div className="border border-amber-300 bg-amber-50 rounded-xl p-3 flex items-start gap-3" data-testid="linea-base-banner">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-800">
              <b>Primera estimación del contrato.</b> Este archivo definirá el presupuesto base de la obra por <b data-testid="linea-base-monto">{mxn(totalPresupuesto)}</b> (partidas y subpartidas quedarán como línea base).
              <label className="flex items-center gap-2 mt-2 font-semibold cursor-pointer">
                <Checkbox data-testid="linea-base-check" checked={baseOk} onCheckedChange={(v) => setBaseOk(!!v)} />
                Confirmo que este archivo define el presupuesto base
              </label>
            </div>
          </div>
        )}

        <div>
          <SectionTitle>Carátula (editable)</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CarInput label="Proyecto" k="nombre_proyecto" />
            <CarInput label="Contratista" k="contratista" />
            <CarInput label="Niveles" k="niveles" />
            <CarInput label="No. estimación" k="numero_estimacion" type="number" />
            <CarInput label="Fecha" k="fecha" type="date" />
            <CarInput label="Periodo del" k="periodo_inicio" type="date" />
            <CarInput label="Periodo al" k="periodo_fin" type="date" />
            <CarInput label="Importe presente estimación" k="importe_presente" type="number" money />
          </div>
          <div className="mt-3 border rounded-lg p-3 bg-slate-50/60">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold mb-2 flex items-center gap-1"><Banknote size={12} /> Ministración</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-400">Fecha última</Label>
                <Input data-testid="min-fecha" type="date" className="h-8 text-xs" value={car.ministracion?.fecha_ultima || ""} onChange={(e) => setMin("fecha_ultima", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-400">Importe ministrado</Label>
                <Input data-testid="min-importe" type="number" className="h-8 text-xs" value={car.ministracion?.importe_periodo ?? ""} onChange={(e) => setMin("importe_periodo", e.target.value === "" ? null : Number(e.target.value))} />
                <p className="text-[10px] text-slate-400">{mxn(car.ministracion?.importe_periodo)}</p></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-400">Ejecutado presente</Label>
                <p className="text-xs font-bold text-[#002B49] pt-2" data-testid="min-ejecutado">{mxn(car.ministracion?.ejecutado_presente)}</p></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-400">Por ejecutar</Label>
                <p className="text-xs font-bold text-[#002B49] pt-2">{mxn(car.ministracion?.por_ejecutar)}</p></div>
            </div>
          </div>
        </div>

        <div>
          <SectionTitle right={
            <div className="text-right">
              <span className="text-[10px] uppercase text-slate-400 mr-2">Presupuesto</span>
              <span className="font-display font-bold text-[#002B49]" data-testid="est-total-presupuesto">{mxn(totalPresupuesto)}</span>
              <span className="text-[10px] uppercase text-slate-400 mx-2">· Ejecutado periodo</span>
              <span className="font-display font-bold text-[#00A3B5]" data-testid="est-total-ejecutado">{mxn(totalEjecutado)}</span>
            </div>}>
            Cuerpo de la estimación {confBaja && <span className="text-[10px] text-amber-600 font-normal ml-2">(confianza baja: revisa los valores marcados)</span>}
          </SectionTitle>
          <div className="overflow-x-auto border rounded-xl">
            <table className="w-full text-xs min-w-[860px]" data-testid="est-cuerpo-tabla">
              <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Partida / Subpartida</th><th className="px-3 py-2 text-right">Presupuesto</th>
                <th className="px-3 py-2 text-right">% periodo</th><th className="px-3 py-2 text-right">% anterior</th>
                <th className="px-3 py-2 text-right">% acum.</th><th className="px-3 py-2 text-right">$ periodo</th>
                <th className="px-3 py-2 text-right">$ a la fecha</th><th className="px-3 py-2">Decisión</th></tr></thead>
              <tbody>
                {partidas.map((p, pi) => (
                  <PartidaRows key={pi} p={p} pi={pi} setSub={setSub} esPrimera={prop.es_primera} confBaja={confBaja} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-between items-center pt-1 sticky bottom-0 bg-white pb-1">
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={descartar} data-testid="est-descartar-btn">Descartar</Button>
          <div className="flex items-center gap-3">
            {bloqueantes.length > 0 && <span className="text-xs text-red-600 font-semibold">Hay alertas bloqueantes: no se puede guardar</span>}
            <Button data-testid="est-confirmar-btn" disabled={saving || bloqueantes.length > 0 || (prop.es_primera && !baseOk)}
              onClick={confirmar} className="bg-[#002B49] hover:bg-[#00395f] text-white gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />} Confirmar y guardar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PartidaRows({ p, pi, setSub, esPrimera, confBaja }) {
  return (
    <>
      <tr className="bg-[#002B49]/[0.04] border-y border-slate-200">
        <td className="px-3 py-2 font-bold text-[#002B49]">{p.nombre}</td>
        <td className="px-3 py-2 text-right font-bold text-[#002B49]">{mxn(p.subpartidas.reduce((s, x) => s + (Number(x.importe_presupuesto) || 0), 0))}</td>
        <td className="px-3 py-2 text-right text-slate-500">{pctFmt(p.pct_periodo)}</td>
        <td className="px-3 py-2 text-right text-slate-500">{pctFmt(p.pct_anterior)}</td>
        <td className="px-3 py-2 text-right text-slate-500">{pctFmt(p.pct_acumulado)}</td>
        <td className="px-3 py-2 text-right font-semibold text-slate-600">{mxn(p.subpartidas.reduce((s, x) => s + (Number(x.importe_presupuesto) || 0) * (Number(x.pct_periodo) || 0), 0))}</td>
        <td className="px-3 py-2 text-right text-slate-500">{mxn(p.imp_acumulado)}</td>
        <td />
      </tr>
      {p.subpartidas.map((x, si) => {
        const nueva = !esPrimera && x.es_nueva;
        const cambio = x.presupuesto_cambiado;
        const warn = confBaja || nueva || cambio;
        return (
          <tr key={si} className={`border-b border-slate-100 ${x.decision === "descartar" ? "opacity-40" : ""} ${warn ? "bg-yellow-50" : ""}`}>
            <td className="px-3 py-1.5 pl-6 text-slate-700">{x.nombre}
              {nueva && <span className="ml-2 text-[9px] font-bold text-white bg-violet-500 px-1.5 py-0.5 rounded uppercase">Nueva / extra</span>}
              {cambio && <span className="ml-2 text-[9px] font-bold text-white bg-amber-500 px-1.5 py-0.5 rounded uppercase" title={`Línea base: ${mxn(x.presupuesto_base)}`}>Presupuesto cambió</span>}
            </td>
            <td className="px-3 py-1.5 text-right">
              <Input type="number" step="0.01" className="h-7 text-xs text-right w-32 ml-auto" data-testid={`sub-presupuesto-${pi}-${si}`}
                value={x.importe_presupuesto ?? ""} onChange={(e) => setSub(pi, si, { importe_presupuesto: e.target.value === "" ? 0 : Number(e.target.value) })} /></td>
            <td className="px-3 py-1.5 text-right">
              <div className="flex items-center justify-end gap-1">
                <Input type="number" step="0.01" min="0" className="h-7 text-xs text-right w-20" data-testid={`sub-pct-${pi}-${si}`}
                  value={x.pct_periodo != null ? +(x.pct_periodo * 100).toFixed(2) : ""}
                  onChange={(e) => setSub(pi, si, { pct_periodo: e.target.value === "" ? 0 : Number(e.target.value) / 100 })} />
                <span className="text-slate-400">%</span>
              </div></td>
            <td className="px-3 py-1.5 text-right text-slate-500">{pctFmt(x.pct_anterior_sistema ?? x.pct_anterior)}</td>
            <td className="px-3 py-1.5 text-right text-slate-500">{pctFmt((x.pct_anterior_sistema ?? x.pct_anterior) + (Number(x.pct_periodo) || 0))}</td>
            <td className="px-3 py-1.5 text-right font-semibold text-[#002B49]">{mxn((Number(x.importe_presupuesto) || 0) * (Number(x.pct_periodo) || 0))}</td>
            <td className="px-3 py-1.5 text-right text-slate-500">{mxn(x.imp_acumulado)}</td>
            <td className="px-3 py-1.5">
              {(nueva || cambio) && (
                <Select value={x.decision || "pendiente"} onValueChange={(v) => setSub(pi, si, { decision: v === "pendiente" ? null : v })}>
                  <SelectTrigger className="h-7 text-[10px] w-36" data-testid={`sub-decision-${pi}-${si}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">— decidir —</SelectItem>
                    {nueva && <SelectItem value="agregar">Agregar como extra</SelectItem>}
                    {cambio && <SelectItem value="aceptar_presupuesto">Aceptar nuevo presupuesto</SelectItem>}
                    {cambio && <SelectItem value="conservar_presupuesto">Conservar original</SelectItem>}
                    <SelectItem value="descartar">Descartar</SelectItem>
                  </SelectContent>
                </Select>)}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function DetalleModal({ estId, onClose, onChanged, canEdit }) {
  const [e, setE] = useState(null);
  const load = () => api.get(`/estimaciones/${estId}`).then((r) => setE(r.data)).catch((err) => toast.error(apiError(err)));
  useEffect(() => { if (estId) load(); }, [estId]); // eslint-disable-line
  if (!estId) return null;
  const sig = e && { confirmada: "aprobada", aprobada: "pagada" }[e.estatus];
  const avanzar = async () => {
    try { await api.patch(`/estimaciones/${estId}/estatus`, { estatus: sig }); toast.success(`Estimación → ${sig}`); load(); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const descargar = async () => {
    const r = await api.get(`/estimaciones/${estId}/archivo`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = e?.nombre_original || "estimacion.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };
  let lastPartida = null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="est-detalle-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">Estimación No. {e?.numero} — detalle</DialogTitle></DialogHeader>
        {!e ? <p className="text-sm text-slate-400">Cargando…</p> : (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
              <span>Fecha: <b>{fmtDate(e.fecha)}</b></span>
              <span>Periodo: <b>{fmtDate(e.periodo_inicio)} → {fmtDate(e.periodo_fin)}</b></span>
              <span>Ejecutado del periodo: <b className="text-[#00A3B5]">{mxn(e.importe_periodo)}</b></span>
              <span>Acumulado: <b>{mxn(e.importe_acumulado)}</b></span>
              <span>Por estimar: <b>{mxn(e.importe_por_estimar)}</b></span>
              <span className={`px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${EST_STYLE[e.estatus]}`} data-testid="est-detalle-estatus">{e.estatus}</span>
            </div>
            {e.ministracion && (
              <p className="text-xs text-slate-500 flex items-center gap-1"><Banknote size={12} className="text-[#00A3B5]" />
                Ministración ligada: <b>{mxn(e.ministracion.importe)}</b> ({fmtDate(e.ministracion.fecha)})</p>)}
            <div className="overflow-x-auto border rounded-xl">
              <table className="w-full text-xs min-w-[760px]">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Subpartida</th><th className="px-3 py-2 text-right">Presupuesto</th>
                  <th className="px-3 py-2 text-right">% periodo</th><th className="px-3 py-2 text-right">% anterior</th>
                  <th className="px-3 py-2 text-right">% acum.</th><th className="px-3 py-2 text-right">$ periodo</th>
                  <th className="px-3 py-2 text-right">$ anterior</th><th className="px-3 py-2 text-right">$ a la fecha</th></tr></thead>
                <tbody>
                  {(e.detalles || []).map((d, i) => {
                    const showPartida = d.partida !== lastPartida; lastPartida = d.partida;
                    return (
                      <React.Fragment key={i}>
                        {showPartida && <tr className="bg-[#002B49]/[0.04]"><td colSpan={8} className="px-3 py-1.5 font-bold text-[#002B49]">{d.partida}</td></tr>}
                        <tr className="border-b border-slate-100">
                          <td className="px-3 py-1.5 pl-6 text-slate-700">{d.subpartida}{d.es_extra && <span className="ml-2 text-[9px] font-bold text-white bg-violet-500 px-1.5 py-0.5 rounded uppercase">Extra</span>}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{mxn(d.importe_presupuesto)}</td>
                          <td className="px-3 py-1.5 text-right">{pctFmt(d.pct_periodo)}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{pctFmt(d.pct_anterior)}</td>
                          <td className="px-3 py-1.5 text-right font-semibold">{pctFmt(d.pct_acumulado)}</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-[#002B49]">{mxn(d.importe_periodo)}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{mxn(d.importe_anterior)}</td>
                          <td className="px-3 py-1.5 text-right">{mxn(d.importe_acumulado)}</td>
                        </tr>
                      </React.Fragment>);
                  })}
                </tbody>
              </table>
            </div>
            {(e.alertas || []).length > 0 && (
              <details className="text-xs"><summary className="cursor-pointer font-semibold text-slate-600">Alertas registradas al confirmar ({e.alertas.length})</summary>
                <div className="mt-2"><AlertasPanel alertas={e.alertas} /></div></details>)}
            <div className="flex justify-between items-center">
              <Button variant="outline" size="sm" className="gap-2" onClick={descargar} data-testid="est-descargar-btn"><Download size={13} /> Archivo original</Button>
              {canEdit && sig && <Button size="sm" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white gap-1" onClick={avanzar} data-testid="est-avanzar-estatus-btn">
                Marcar como {sig} <ChevronRight size={13} /></Button>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function EstimacionesObra({ pid }) {
  const { canEdit, isAdmin } = useAuth();
  const [contratos, setContratos] = useState(null);
  const [sel, setSel] = useState(null);
  const [ests, setEsts] = useState([]);
  const [mins, setMins] = useState([]);
  const [showContrato, setShowContrato] = useState(false);
  const [editContrato, setEditContrato] = useState(null);
  const [analisis, setAnalisis] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [detalleId, setDetalleId] = useState(null);
  const [minForm, setMinForm] = useState(null);
  const fileRef = useRef();
  const pollRef = useRef();

  const loadContratos = () => api.get(`/properties/${pid}/contratos-obra`).then((r) => {
    setContratos(r.data);
    if (r.data.length && !r.data.find((c) => c.id === sel)) setSel(r.data[0].id);
  });
  const loadDatos = (cid) => {
    if (!cid) return;
    api.get(`/contratos-obra/${cid}/estimaciones`).then((r) => setEsts(r.data));
    api.get(`/contratos-obra/${cid}/ministraciones`).then((r) => setMins(r.data));
  };
  useEffect(() => { loadContratos(); return () => clearInterval(pollRef.current); }, [pid]); // eslint-disable-line
  useEffect(() => { loadDatos(sel); }, [sel]); // eslint-disable-line

  const contrato = contratos?.find((c) => c.id === sel);
  const refresh = () => { loadContratos(); loadDatos(sel); };

  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !sel) return;
    const fd = new FormData(); fd.append("file", file);
    setAnalizando(true);
    try {
      const r = await api.post(`/contratos-obra/${sel}/estimaciones/analizar`, fd);
      const aid = r.data.analisis_id;
      pollRef.current = setInterval(async () => {
        const a = (await api.get(`/est-analisis/${aid}`)).data;
        if (a.estado === "listo") { clearInterval(pollRef.current); setAnalizando(false); setAnalisis(a); }
        if (a.estado === "error") { clearInterval(pollRef.current); setAnalizando(false); toast.error(a.error || "No fue posible leer el archivo"); }
      }, 2000);
    } catch (err) { setAnalizando(false); toast.error(apiError(err)); }
  };

  const guardarMin = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/contratos-obra/${sel}/ministraciones`, { fecha: minForm.fecha, importe: Number(minForm.importe),
        periodo_inicio: minForm.periodo_inicio || null, periodo_fin: minForm.periodo_fin || null });
      toast.success("Ministración registrada"); setMinForm(null); refresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  const borrarEst = async (x) => {
    if (!window.confirm(`¿Eliminar la estimación No. ${x.numero}? Se recalcularán los acumulados.`)) return;
    try { await api.delete(`/estimaciones/${x.id}`); toast.success("Estimación eliminada"); refresh(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (contratos === null) return <p className="text-sm text-slate-400 py-8">Cargando…</p>;

  return (
    <div data-testid="estimaciones-tab">
      {contratos.length === 0 ? (
        <Card className="p-10 text-center">
          <FileSpreadsheet size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-1 font-semibold">Sin contratos de obra</p>
          <p className="text-xs text-slate-400 mb-4 max-w-md mx-auto">Crea el contrato del contratista y sube su primera estimación en Excel: la IA la lee, tú la confirmas y queda como línea base del presupuesto.</p>
          {canEdit && <Button data-testid="nuevo-contrato-btn" onClick={() => { setEditContrato(null); setShowContrato(true); }} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white gap-2"><Plus size={14} /> Crear contrato de obra</Button>}
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Contrato / contratista</Label>
              <Select value={sel || ""} onValueChange={setSel}>
                <SelectTrigger className="w-64" data-testid="contrato-select"><SelectValue /></SelectTrigger>
                <SelectContent>{contratos.map((c) => <SelectItem key={c.id} value={c.id}>{c.contratista}{c.nombre_proyecto ? ` — ${c.nombre_proyecto}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {canEdit && <>
              <Button variant="outline" size="sm" onClick={() => { setEditContrato(contrato); setShowContrato(true); }}>Editar contrato</Button>
              <Button variant="outline" size="sm" onClick={() => { setEditContrato(null); setShowContrato(true); }} className="gap-1" data-testid="nuevo-contrato-btn"><Plus size={13} /> Nuevo contrato</Button>
              <Button variant="outline" size="sm" onClick={() => setMinForm({ fecha: new Date().toISOString().slice(0, 10) })} className="gap-1" data-testid="nueva-ministracion-btn"><Banknote size={13} /> Registrar ministración</Button>
              <div className="flex-1" />
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" className="hidden" onChange={subir} data-testid="est-upload-input" />
              <Button data-testid="subir-estimacion-btn" disabled={analizando} onClick={() => fileRef.current?.click()} className="bg-[#002B49] hover:bg-[#00395f] text-white gap-2">
                {analizando ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {analizando ? "Leyendo con IA…" : "Subir estimación"}
              </Button>
            </>}
          </div>

          {contrato && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[["Presupuesto del contrato", contrato.presupuesto_total, "presupuesto"],
                ["Ejecutado acumulado", contrato.ejecutado_acumulado, "ejecutado"],
                ["Ministrado acumulado", contrato.ministrado_acumulado, "ministrado"],
                ["Ministrado sin ejecutar", (contrato.ministrado_acumulado || 0) - (contrato.ejecutado_acumulado || 0), "sin-ejecutar"]].map(([t, v, k]) => (
                <Card key={k} className="p-3" testId={`kpi-${k}`}>
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t}</p>
                  <p className={`font-display text-lg font-bold ${k === "sin-ejecutar" && v > 0.2 * (mins[mins.length - 1]?.importe || Infinity) ? "text-red-600" : "text-[#002B49]"}`}>{v != null ? mxn(v) : "—"}</p>
                </Card>))}
            </div>
          )}

          <Card className="overflow-hidden mb-4" testId="tabla-estimaciones">
            <div className="px-4 pt-4"><SectionTitle>Estimaciones</SectionTitle></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5">No.</th><th className="px-4 py-2.5">Fecha</th><th className="px-4 py-2.5">Periodo</th>
                  <th className="px-4 py-2.5 text-right">Importe periodo</th><th className="px-4 py-2.5 text-right">Acumulado</th>
                  <th className="px-4 py-2.5 text-right">Ministración</th><th className="px-4 py-2.5">Alertas</th>
                  <th className="px-4 py-2.5">Estatus</th><th className="px-4 py-2.5" /></tr></thead>
                <tbody>
                  {ests.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400 text-xs">Sin estimaciones. Sube el Excel del contratista para leerlo con IA.</td></tr>}
                  {ests.map((x) => (
                    <tr key={x.id} className="border-b border-slate-100" data-testid={`est-row-${x.numero}`}>
                      <td className="px-4 py-2.5 font-bold text-[#002B49]">{x.numero}</td>
                      <td className="px-4 py-2.5 text-slate-600">{fmtDate(x.fecha)}</td>
                      <td className="px-4 py-2.5 text-slate-600 text-xs">{fmtDate(x.periodo_inicio)} → {fmtDate(x.periodo_fin)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-[#00A3B5]">{mxn(x.importe_periodo)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600">{mxn(x.importe_acumulado)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600">{x.ministracion ? mxn(x.ministracion.importe) : "—"}</td>
                      <td className="px-4 py-2.5">{x.num_alertas > 0 ? <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">{x.num_alertas}</span> : <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${EST_STYLE[x.estatus]}`}>{x.estatus}</span></td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button className="text-slate-400 hover:text-[#00A3B5] mr-2" title="Ver detalle" onClick={() => setDetalleId(x.id)} data-testid={`est-ver-${x.numero}`}><Eye size={15} /></button>
                        {isAdmin && <button className="text-slate-300 hover:text-red-500" title="Eliminar" onClick={() => borrarEst(x)} data-testid={`est-borrar-${x.numero}`}><Trash2 size={14} /></button>}
                      </td>
                    </tr>))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="overflow-hidden" testId="tabla-ministraciones">
            <div className="px-4 pt-4"><SectionTitle>Ministraciones</SectionTitle></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5">Fecha</th><th className="px-4 py-2.5 text-right">Importe</th>
                  <th className="px-4 py-2.5">Periodo</th><th className="px-4 py-2.5">Origen</th><th className="px-4 py-2.5" /></tr></thead>
                <tbody>
                  {mins.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400 text-xs">Sin ministraciones registradas.</td></tr>}
                  {mins.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100">
                      <td className="px-4 py-2.5 text-slate-600">{fmtDate(m.fecha)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-[#002B49]">{mxn(m.importe)}</td>
                      <td className="px-4 py-2.5 text-slate-600 text-xs">{m.periodo_inicio ? `${fmtDate(m.periodo_inicio)} → ${fmtDate(m.periodo_fin)}` : "—"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{m.estimacion_id ? "Estimación" : "Captura manual"}</td>
                      <td className="px-4 py-2.5 text-right">{isAdmin && !m.estimacion_id && <button className="text-slate-300 hover:text-red-500" onClick={async () => { if (window.confirm("¿Eliminar ministración?")) { try { await api.delete(`/ministraciones/${m.id}`); refresh(); } catch (err) { toast.error(apiError(err)); } } }}><Trash2 size={14} /></button>}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <ContratoModal pid={pid} contrato={editContrato} open={showContrato} onClose={() => setShowContrato(false)} onSaved={refresh} />
      {analisis && <ConfirmacionModal analisis={analisis} contrato={contrato} onClose={() => setAnalisis(null)} onConfirmado={refresh} />}
      {detalleId && <DetalleModal estId={detalleId} onClose={() => setDetalleId(null)} onChanged={refresh} canEdit={canEdit} />}

      {minForm && (
        <Dialog open onOpenChange={(v) => !v && setMinForm(null)}>
          <DialogContent className="max-w-sm" data-testid="ministracion-modal">
            <DialogHeader><DialogTitle className="font-display text-[#002B49]">Registrar ministración</DialogTitle></DialogHeader>
            <form onSubmit={guardarMin} className="space-y-3">
              <div className="space-y-1.5"><Label>Fecha *</Label>
                <Input type="date" required value={minForm.fecha || ""} onChange={(e) => setMinForm({ ...minForm, fecha: e.target.value })} data-testid="min-form-fecha" /></div>
              <div className="space-y-1.5"><Label>Importe (MXN) *</Label>
                <Input type="number" step="0.01" min="0.01" required value={minForm.importe || ""} onChange={(e) => setMinForm({ ...minForm, importe: e.target.value })} data-testid="min-form-importe" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Periodo del</Label>
                  <Input type="date" value={minForm.periodo_inicio || ""} onChange={(e) => setMinForm({ ...minForm, periodo_inicio: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>al</Label>
                  <Input type="date" value={minForm.periodo_fin || ""} onChange={(e) => setMinForm({ ...minForm, periodo_fin: e.target.value })} /></div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setMinForm(null)}>Cancelar</Button>
                <Button type="submit" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white" data-testid="min-form-guardar">Guardar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
